class ShippingCalculatorError extends Error {}

// Shopify's /cart/shipping_rates.json resolves shipping_address[country] and
// [province] by matching the full English name (see shopify.dev Ajax Cart API
// reference), not the ISO code — ViaCEP only gives us the UF abbreviation.
const BRAZIL_STATE_NAMES = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
};

if (!customElements.get('shipping-calculator-component')) {
  customElements.define(
    'shipping-calculator-component',
    class ShippingCalculatorComponent extends HTMLElement {
      // Tracks the zip/quantity pair the last calculation ran for, so the
      // button can be locked afterwards and only re-armed once the customer
      // actually changes one of those two inputs (rather than allowing
      // repeated clicks that would just add/restore the cart over and over
      // for no new information).
      lastCalculated = null;

      constructor() {
        super();
        this.form = this.querySelector('.shipping-calculator__form');
        this.input = this.querySelector('.shipping-calculator__input');
        this.button = this.querySelector('.shipping-calculator__button');
        this.spinner = this.querySelector('.loading__spinner');
        this.resultsEl = this.querySelector('.shipping-calculator__results');

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.input.addEventListener('input', this.onZipInput.bind(this));
      }

      connectedCallback() {
        this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
        this.quantityInput?.addEventListener('change', this.onRelevantInputChange.bind(this));
        this.quantityInput?.addEventListener('input', this.onRelevantInputChange.bind(this));
        // 'input' (not just 'change') so re-arming happens while typing a new
        // zip, not only after the field loses focus.
        this.input.addEventListener('input', this.onRelevantInputChange.bind(this));
      }

      onZipInput(event) {
        const digits = event.target.value.replace(/\D/g, '').slice(0, 8);
        event.target.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
      }

      // Re-arms the "Calcular" button once the zip or quantity actually
      // differs from what the last calculation used.
      onRelevantInputChange() {
        if (!this.lastCalculated) return;

        const zip = this.input.value.replace(/\D/g, '');
        const changed = zip !== this.lastCalculated.zip || this.currentQuantity !== this.lastCalculated.quantity;
        if (changed) {
          this.button.disabled = false;
          this.lastCalculated = null;
        }
      }

      get sectionId() {
        return this.dataset.sectionId;
      }

      get strings() {
        return window.shippingCalculatorStrings || {};
      }

      get currentVariantId() {
        const idInput = document.querySelector(`#${this.dataset.productFormId} input[name="id"]`);
        const value = idInput?.value || this.dataset.variantId;
        return value ? Number(value) : null;
      }

      get currentQuantity() {
        const qtyInput = document.getElementById(`Quantity-${this.sectionId}`);
        const value = qtyInput ? parseInt(qtyInput.value, 10) : NaN;
        return Number.isNaN(value) || value < 1 ? 1 : value;
      }

      get cartUrl() {
        return `${window.routes.cart_url}.js`;
      }

      get shippingRatesUrl() {
        // Derived from cart_url (not cart_add_url) because cart_add_url isn't
        // guaranteed to end in ".js" (on this store it's just "/cart/add"),
        // which previously made this silently resolve to the wrong endpoint.
        return `${window.routes.cart_url}/shipping_rates.json`;
      }

      // Shopify resolves the destination address by matching against its own
      // country/province name list, so we look up the full address behind the
      // CEP (via ViaCEP) instead of only sending the zip — the same amount of
      // address detail checkout collects before Frenet returns rates there.
      // ViaCEP is a "nice to have" enrichment: if it's unreachable or the CEP
      // isn't found there, we still fall through to a zip+country-only lookup
      // instead of blocking the whole calculation on a third-party dependency.
      async lookupAddress(zip) {
        try {
          const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
          const data = await response.json();

          if (!response.ok || data.erro) {
            return { zip, city: '', province: '', country: 'BR' };
          }

          return {
            zip,
            city: data.localidade,
            province: BRAZIL_STATE_NAMES[data.uf] || data.uf,
            country: 'Brazil',
          };
        } catch (error) {
          console.error('ViaCEP lookup failed, falling back to zip-only address', error);
          return { zip, city: '', province: '', country: 'BR' };
        }
      }

      buildAddressParams(address) {
        // Shopify's parameter validator rejects the request outright (400
        // "Parameter Missing or Invalid") if a shipping_address key is present
        // but blank, so only send keys that actually have a value.
        return [
          ['shipping_address[zip]', address.zip],
          ['shipping_address[country]', address.country],
          ['shipping_address[province]', address.province],
          ['shipping_address[city]', address.city],
        ]
          .filter(([, value]) => value)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join('&');
      }

      // Restores the cart line for `variantId` back to `quantity` after the
      // temporary add used to compute rates. Verifies the result against the
      // response body (rather than firing-and-forgetting) and retries once,
      // since a silently failed/ignored restore is exactly what would leave
      // stray units in the customer's cart after every calculation.
      async restoreCartQuantity(variantId, quantity, attempt = 1) {
        const response = await fetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: String(variantId), quantity }),
        });

        const body = await response.text();
        let resultingQuantity = null;

        if (response.ok) {
          try {
            const cart = JSON.parse(body);
            const line = cart.items.find((item) => item.variant_id === variantId);
            resultingQuantity = line ? line.quantity : 0;
          } catch {
            resultingQuantity = null;
          }
        }

        if (resultingQuantity === quantity) return null;

        console.error('cart/change.js restore mismatch', { attempt, variantId, quantity, resultingQuantity, status: response.status, body });

        if (attempt === 1) {
          return this.restoreCartQuantity(variantId, quantity, 2);
        }

        return `Falha ao restaurar o carrinho: esperado ${quantity}, ficou ${resultingQuantity} (HTTP ${response.status})`;
      }

      async onSubmit(event) {
        event.preventDefault();

        const zip = this.input.value.replace(/\D/g, '');
        if (zip.length !== 8) {
          this.renderError(this.strings.invalidZip);
          return;
        }

        const variantId = this.currentVariantId;
        if (!variantId) {
          this.renderError(this.strings.unavailable);
          return;
        }

        this.setLoading(true);

        const targetQuantity = this.currentQuantity;

        // The calculation always reflects the quantity selected in the
        // stepper, not whatever happens to already be in the cart. The cart
        // line is only touched when it doesn't already match that target,
        // and always put back exactly how it was found afterwards.
        let needsRestore = false;
        let existingQuantity = 0;
        let hadOtherItems = false;

        try {
          const address = await this.lookupAddress(zip);

          const cart = await fetch(this.cartUrl, { cache: 'no-store' }).then((response) => response.json());
          const existingLine = cart.items.find((item) => item.variant_id === variantId);
          existingQuantity = existingLine ? existingLine.quantity : 0;
          hadOtherItems = cart.items.some((item) => item.variant_id !== variantId);

          if (existingQuantity !== targetQuantity) {
            // add.js is required to create the line when it doesn't exist yet;
            // change.js is required to set an absolute quantity on a line that
            // already exists (add.js would only ever increment it).
            const url = existingQuantity === 0 ? window.routes.cart_add_url : window.routes.cart_change_url;
            const setResponse = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: String(variantId), quantity: targetQuantity }),
            });

            if (!setResponse.ok) {
              const body = await setResponse.text().catch(() => '');
              console.error('cart update failed', url, setResponse.status, body);
              const error = new ShippingCalculatorError('unavailable');
              error.detail = `HTTP ${setResponse.status}: ${body.slice(0, 200)}`;
              throw error;
            }

            needsRestore = true;
          }

          // cache: 'no-store' plus a cache-busting param, since this URL is
          // identical across calculations for the same zip — without both, the
          // browser (or an intermediate cache) can serve a stale response and
          // hide the fact that the cart's contents/quantity changed.
          const ratesUrl = `${this.shippingRatesUrl}?${this.buildAddressParams(address)}&_=${Date.now()}`;
          const ratesResponse = await fetch(ratesUrl, { cache: 'no-store' });
          const rawBody = await ratesResponse.text();

          if (!ratesResponse.ok) {
            console.error('shipping_rates.json failed', ratesResponse.status, rawBody, ratesUrl);
            const error = new ShippingCalculatorError('genericError');
            error.detail = `${ratesUrl} -> HTTP ${ratesResponse.status}: ${rawBody.slice(0, 160)}`;
            throw error;
          }

          const rates = JSON.parse(rawBody).shipping_rates || [];
          console.debug('shipping_rates.json response', rates, ratesUrl);

          const restoreDetail = needsRestore ? await this.restoreCartQuantity(variantId, existingQuantity) : null;

          if (!rates.length) {
            this.renderError(this.strings.noRates);
            return;
          }

          this.lastCalculated = { zip, quantity: targetQuantity };
          this.renderResults(rates, hadOtherItems, restoreDetail);
        } catch (error) {
          console.error(error);
          if (needsRestore) {
            // best-effort cleanup so a failed calculation never leaves the cart in the temporary state
            fetch(window.routes.cart_change_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: String(variantId), quantity: existingQuantity }),
            }).catch(() => {});
          }

          const key = error instanceof ShippingCalculatorError ? error.message : 'genericError';
          const detail = error?.detail || (key === 'genericError' ? error?.message : '');
          this.renderError(this.strings[key], detail);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        // Once locked (this.lastCalculated set), stay disabled after loading
        // ends too — only onRelevantInputChange re-arms it.
        this.button.disabled = isLoading || this.lastCalculated !== null;
        this.input.disabled = isLoading;
        this.spinner?.classList.toggle('hidden', !isLoading);
        this.button.querySelector('.shipping-calculator__button-text').textContent = isLoading
          ? this.strings.calculating
          : this.strings.button;
      }

      formatPrice(rate) {
        // /cart/shipping_rates.json returns price as a decimal string (e.g. "24.90"), not cents
        const amount = Number(rate.price);
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
            style: 'currency',
            currency: rate.currency || 'BRL',
          }).format(amount);
        } catch {
          return `R$ ${amount.toFixed(2).replace('.', ',')}`;
        }
      }

      // /cart/shipping_rates.json rate objects carry the carrier/service label
      // as "name" (not "title"/"presentment_title" like the Checkout GraphQL
      // API) — fall back across the variants seen in the wild just in case.
      rateName(rate) {
        return rate.name || rate.presentment_title || rate.title || rate.code || '';
      }

      formatDeliveryEstimate(rate) {
        if (rate.min_delivery_date && rate.max_delivery_date) {
          const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'pt-BR', {
            day: '2-digit',
            month: '2-digit',
          });
          const min = formatter.format(new Date(rate.min_delivery_date));
          const max = formatter.format(new Date(rate.max_delivery_date));

          return min === max
            ? this.strings.deliveryOn?.replace('{{ date }}', min)
            : this.strings.deliveryBetween?.replace('{{ min }}', min).replace('{{ max }}', max);
        }

        const haystack = `${this.rateName(rate)} ${rate.description || ''}`;
        const daysMatch = haystack.match(/(\d+)\s*(?:a|à|-|até)\s*(\d+)\s*dias/i);
        if (daysMatch) {
          return this.strings.deliveryDays?.replace('{{ min }}', daysMatch[1]).replace('{{ max }}', daysMatch[2]);
        }

        return '';
      }

      renderResults(rates, hadOtherItems, restoreDetail) {
        const sortedRates = [...rates].sort((a, b) => Number(a.price) - Number(b.price));

        const items = sortedRates
          .map((rate) => {
            const delivery = this.formatDeliveryEstimate(rate);
            return `
              <li class="shipping-calculator__rate">
                <span class="shipping-calculator__rate-info">
                  <span class="shipping-calculator__rate-name">${this.escapeHtml(this.rateName(rate))}</span>
                  ${delivery ? `<span class="shipping-calculator__rate-delivery">${this.escapeHtml(delivery)}</span>` : ''}
                </span>
                <span class="shipping-calculator__rate-price">${this.escapeHtml(this.formatPrice(rate))}</span>
              </li>
            `;
          })
          .join('');

        this.resultsEl.innerHTML = `
          <ul class="shipping-calculator__rate-list">${items}</ul>
          ${hadOtherItems ? `<p class="shipping-calculator__note">${this.escapeHtml(this.strings.cartNote)}</p>` : ''}
          ${restoreDetail ? `<p class="shipping-calculator__detail">${this.escapeHtml(restoreDetail)}</p>` : ''}
        `;
        this.resultsEl.hidden = false;
      }

      // `detail` is a temporary debugging aid (raw HTTP status/body) shown
      // directly on the page so failures can be diagnosed from a screenshot
      // without needing to open devtools. Safe to remove once this is stable.
      renderError(message, detail) {
        const detailHtml = detail
          ? `<p class="shipping-calculator__detail">${this.escapeHtml(detail)}</p>`
          : '';
        this.resultsEl.innerHTML = `<p class="shipping-calculator__message shipping-calculator__message--error">${this.escapeHtml(
          message || this.strings.genericError
        )}</p>${detailHtml}`;
        this.resultsEl.hidden = false;
      }

      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value ?? '';
        return div.innerHTML;
      }
    }
  );
}
